import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, message, Button, Dropdown, Space,
  Tooltip, Drawer, Divider, Modal, Form, Select,
  DatePicker, InputNumber, Radio, Alert, Spin, AutoComplete, Pagination
} from "antd";
import {
  SearchOutlined, CrownOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined,
  UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined,
  IdcardOutlined, TrophyOutlined, StarFilled, FireOutlined,
  CheckCircleOutlined, ClockCircleOutlined, DollarOutlined,
  EnvironmentOutlined, WhatsAppOutlined, CopyOutlined,
  GiftOutlined, PlusCircleOutlined, CloseCircleOutlined,
  HistoryOutlined, ThunderboltOutlined, SafetyOutlined,
  RiseOutlined, WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getPremiumMembers,
  getPremiumPlans,
  UserGet,
  UserUpdate,
  UserTransactionsPost,
  UserTotalRevenuePost,
  grantPremiumToUser,
  extendPremiumForUser,
  removePremiumFromUser,
  getPremiumGrantHistory,
  sendPremiumSMS,
  createUserNotification,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, generateRefNumber, capitalize,
} from "../../Utils/formatters";
import Swal from "sweetalert2";
import "./PremiumMembers.css";

const { Option } = Select;
const { TextArea } = Input;

// ✅ Plan Type Configs
const PLAN_TYPE_CONFIG = {
  contact_view: { icon: '📱', label: 'Contact View', color: '#3b82f6' },
  specific_user: { icon: '👤', label: 'Specific User', color: '#8b5cf6' },
  profile_boost: { icon: '🚀', label: 'Profile Boost', color: '#f59e0b' },
  custom: { icon: '⚙️', label: 'Custom', color: '#10b981' },
};

// ✅ Payment Mode Configs
const PAYMENT_MODES = [
  { value: 'cash', label: '💵 Cash Received', color: '#10b981' },
  { value: 'online', label: '🏦 Online Payment', color: '#3b82f6' },
  { value: 'upi', label: '📱 UPI Transfer', color: '#8b5cf6' },
  { value: 'bank_transfer', label: '🏛️ Bank Transfer', color: '#6366f1' },
  { value: 'gift', label: '🎁 Free / Complimentary', color: '#ec4899' },
  { value: 'custom', label: '⚙️ Custom / Other', color: '#f59e0b' },
];

function PremiumMembers() {
  // ============================================
  // STATES
  // ============================================
  const [members, setMembers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // ✅ Grant Premium States
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [freeUsers, setFreeUsers] = useState([]);
  const [premiumPlans, setPremiumPlans] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [customAmount, setCustomAmount] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [grantForm] = Form.useForm();

  // ✅ Grant History States
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [grantHistory, setGrantHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ✅ Extend/Remove States
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendUser, setExtendUser] = useState(null);
  const [extendDays, setExtendDays] = useState(30);

  const adminName = localStorage.getItem("username") || "Admin";

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 📱 Mobile Responsiveness States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileStats, setShowMobileStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================
  // FETCH DATA
  // ============================================
  const fetchPremium = async () => {
    try {
      setLoading(true);
      const [premiumData, plansData, allUsersData] = await Promise.all([
        getPremiumMembers(),
        getPremiumPlans(),
        UserGet(),
      ]);

      const premiumMembers = Array.isArray(premiumData)
        ? premiumData.filter(m => m.is_premium === true || m.premium === true)
        : [];

      const allUsersArr = Array.isArray(allUsersData) ? allUsersData : [];
      const freeUsersArr = allUsersArr.filter(u =>
        u.role === "user" && !u.is_premium && !u.premium
      );

      setMembers(premiumMembers);
      setFiltered(premiumMembers);
      setPremiumPlans(Array.isArray(plansData) ? plansData.filter(p => p.status === 'active') : []);
      setAllUsers(allUsersArr);
      setFreeUsers(freeUsersArr);
    } catch (error) {
      console.error("❌ Error:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPremium(); }, []);

  // ============================================
  // FILTER
  // ============================================
  useEffect(() => {
    let filteredList = [...members];

    if (searchText) {
      filteredList = filteredList.filter(m =>
        m.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.contact?.includes(searchText) ||
        (m.profile_id && m.profile_id.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    if (planFilter !== "all") {
      filteredList = filteredList.filter(m => {
        if (m.all_plans && Array.isArray(m.all_plans)) {
          return m.all_plans.some(p => (p.plan_name || "").toLowerCase().includes(planFilter));
        }
        return (m.plan_name || "premium").toLowerCase().includes(planFilter);
      });
    }

    setFiltered(filteredList);
    setCurrentPage(1);
  }, [searchText, planFilter, members]);

  // ============================================
  // HELPERS
  // ============================================
  const getPlanConfig = (planName) => {
    const plan = (planName || "premium").toLowerCase();
    if (plan.includes("platinum") || plan.includes("diamond")) {
      return { color: "platinum", icon: <CrownOutlined />, label: capitalize(planName) };
    }
    if (plan.includes("gold")) {
      return { color: "gold", icon: <TrophyOutlined />, label: capitalize(planName) };
    }
    if (plan.includes("silver")) {
      return { color: "silver", icon: <StarFilled />, label: capitalize(planName) };
    }
    return { color: "premium", icon: <CrownOutlined />, label: capitalize(planName) || "Premium" };
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  // ============================================
  // ✅ GRANT PREMIUM HANDLER
  // ============================================
  const handleOpenGrantModal = () => {
    grantForm.resetFields();
    setSelectedUser(null);
    setSelectedPlan(null);
    setPaymentMode('cash');
    setCustomAmount(null);
    setStartDate(dayjs());
    setGrantModalOpen(true);
  };

  const handleUserSelect = (userId) => {
    let user = freeUsers.find(u => String(u.id) === String(userId));
    if (!user) user = members.find(m => String(m.id) === String(userId));
    setSelectedUser(user);
  };

  const handlePlanSelect = (planId) => {
    const plan = premiumPlans.find(p => String(p.id) === String(planId));
    setSelectedPlan(plan);
    if (plan) setCustomAmount(plan.price);
  };

  const calculateEndDate = () => {
    if (!selectedPlan || !startDate) return null;
    return dayjs(startDate).add(selectedPlan.duration || 30, 'day');
  };

  const checkExistingPlan = () => {
    if (!selectedUser || !selectedPlan) return null;
    const existingMember = members.find(m => String(m.id) === String(selectedUser.id));
    if (!existingMember || !existingMember.all_plans) return null;
    return existingMember.all_plans.find(p => String(p.plan_id) === String(selectedPlan.id));
  };

  const existingPlan = checkExistingPlan();

  const handleGrantPremium = async () => {
    try {
      if (!selectedUser) return message.error("Please select a user");
      if (!selectedPlan) return message.error("Please select a plan");

      const values = await grantForm.validateFields();
      const isExtending = !!existingPlan;
      const confirmTitle = isExtending ? '⭐ Extend Existing Plan?' : '⭐ Grant Premium?';
      const confirmText = isExtending
        ? `This will EXTEND the existing "${selectedPlan.plan_name}" plan by ${selectedPlan.duration} days.`
        : `This will grant premium plan to user.`;

      const confirmed = await Swal.fire({
        title: confirmTitle,
        html: `
        <div style="text-align:left; padding:10px;">
          <p style="color:#666; margin-bottom:8px; font-size:13px;">${confirmText}</p>
          <p><strong>User:</strong> ${selectedUser.first_name || selectedUser.username}</p>
          <p><strong>Plan:</strong> ${selectedPlan.plan_name}</p>
          <p><strong>Duration:</strong> ${selectedPlan.duration} days ${isExtending ? '(added to existing)' : ''}</p>
          <p><strong>Amount:</strong> ₹${customAmount || 0}</p>
          <p><strong>Payment:</strong> ${PAYMENT_MODES.find(p => p.value === paymentMode)?.label}</p>
          ${isExtending ? `
            <div style="background:#fef3c7; padding:8px; border-radius:6px; margin-top:10px; font-size:12px; color:#92400e;">
              ⚠️ <strong>Extension Mode:</strong><br/>
              Current expiry: ${formatDate(existingPlan.plan_expiry)}<br/>
              New expiry: ${dayjs(existingPlan.plan_expiry).add(selectedPlan.duration, 'day').format('DD MMM YYYY')}
            </div>
          ` : ''}
        </div>
      `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff0055',
        confirmButtonText: isExtending ? '✅ Extend Plan' : '✅ Grant Premium',
      });

      if (!confirmed.isConfirmed) return;

      setGrantLoading(true);
      const endDate = calculateEndDate();
      const finalAmount = paymentMode === 'gift' ? 0 : (customAmount || selectedPlan.price);

      try {
        const grantResponse = await grantPremiumToUser({
          user_id: selectedUser.id,
          plan_id: selectedPlan.id,
          amount: finalAmount,
          payment_mode: paymentMode,
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          admin_note: values.admin_note || '',
        });

        try { await sendPremiumSMS(selectedUser.id, selectedPlan.plan_name); } catch (smsErr) { }

        const wasExtension = grantResponse.is_extension || false;
        Swal.fire({
          title: wasExtension ? '🎉 Plan Extended!' : '🎉 Premium Granted!',
          html: `
          <div style="text-align:center;">
            <p style="font-size:16px; color:#333; margin:10px 0;">
              ${wasExtension ? 'Plan extended for' : 'Premium granted to'} <strong>${selectedUser.first_name || selectedUser.username}</strong>
            </p>
            <p style="color:#666; margin:8px 0;"><strong>Plan:</strong> ${selectedPlan.plan_name}</p>
            <p style="color:#666; margin:8px 0;"><strong>${wasExtension ? 'New Expiry' : 'Expires'}:</strong> ${grantResponse.expiry_date ? dayjs(grantResponse.expiry_date).format('DD MMM YYYY') : endDate.format('DD MMM YYYY')}</p>
          </div>
        `,
          icon: 'success',
          confirmButtonColor: '#ff0055',
        });
        setGrantModalOpen(false);
        fetchPremium();
      } catch (grantError) {
        const errorMsg = grantError?.response?.data?.error || grantError?.response?.data?.message || 'Grant failed';
        Swal.fire({ title: '❌ Grant Failed', text: errorMsg, icon: 'error', confirmButtonColor: '#dc2626' });
      }
    } catch (error) { message.error(error?.message || "Failed to grant premium"); }
    finally { setGrantLoading(false); }
  };

  const handleExtendPremium = async () => {
    if (!extendUser) return;
    try {
      const userPlans = extendUser.all_plans || [];
      if (userPlans.length === 0) {
        message.error("This user has no active plans to extend");
        setExtendModalOpen(false); return;
      }
      let planToExtend = userPlans[0];

      if (userPlans.length > 1) {
        const { value: selectedPlanId } = await Swal.fire({
          title: '📦 Which plan to extend?',
          input: 'select',
          inputOptions: userPlans.reduce((acc, p) => { acc[p.plan_id] = `${p.plan_name} - Expires: ${formatDate(p.plan_expiry)}`; return acc; }, {}),
          showCancelButton: true, confirmButtonColor: '#ff0055', confirmButtonText: 'Continue →',
        });
        if (!selectedPlanId) return;
        planToExtend = userPlans.find(p => String(p.plan_id) === String(selectedPlanId));
      }

      const confirmed = await Swal.fire({
        title: '⭐ Extend Premium?',
        html: `
        <div style="text-align:left; padding:10px;">
          <p><strong>User:</strong> ${extendUser.first_name || extendUser.username}</p>
          <p><strong>Plan:</strong> ${planToExtend.plan_name}</p>
          <p><strong>Extend By:</strong> ${extendDays} days</p>
          <div style="background:#fef3c7; padding:10px; border-radius:6px; margin-top:10px; font-size:13px;">
            <div><strong>Current Expiry:</strong> ${formatDate(planToExtend.plan_expiry)}</div>
            <div style="color:#059669; font-weight:700;"><strong>New Expiry:</strong> ${dayjs(planToExtend.plan_expiry).add(extendDays, 'day').format('DD MMM YYYY')}</div>
          </div>
        </div>
      `,
        icon: 'question', showCancelButton: true, confirmButtonColor: '#16a34a', confirmButtonText: '✅ Extend Now',
      });
      if (!confirmed.isConfirmed) return;

      const actualPlan = premiumPlans.find(p => String(p.id) === String(planToExtend.plan_id));
      if (!actualPlan) return message.error("Plan not found in active plans list");

      const today = dayjs();
      const endDate = dayjs(planToExtend.plan_expiry).add(extendDays, 'day');

      try {
        await grantPremiumToUser({
          user_id: extendUser.id, plan_id: actualPlan.id, amount: 0, payment_mode: 'gift',
          start_date: today.format('YYYY-MM-DD'), end_date: endDate.format('YYYY-MM-DD'),
          admin_note: `Quick extension by ${adminName} - Added ${extendDays} days`,
        });
        Swal.fire({ title: '🎉 Extended!', text: `Plan extended by ${extendDays} days`, icon: 'success', confirmButtonColor: '#ff0055' });
        setExtendModalOpen(false); setExtendUser(null); fetchPremium();
      } catch (extendError) { message.error(extendError?.response?.data?.error || "Failed to extend"); }
    } catch (error) { message.error("Failed to extend premium"); }
  };

  const handleRemovePremium = (member) => {
    Swal.fire({
      title: '❌ Remove Premium?',
      html: `
        <div style="text-align:left;">
          <p>Are you sure you want to remove premium from:</p>
          <p style="background:#fef2f2; padding:10px; border-radius:8px; margin:10px 0;">
            <strong>${member.first_name || member.username}</strong><br/>
            <small>Plans: ${member.total_plans || 1} active plan(s)</small>
          </p>
          <p style="color:#dc2626; font-size:13px;">⚠️ This will immediately deactivate ALL premium plans for this user.</p>
        </div>
      `,
      icon: 'warning', input: 'textarea', inputPlaceholder: 'Reason for removal (optional)...',
      showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Yes, Remove',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await UserUpdate(member.id, {
            is_premium: false, premium: false, membership_status: 'cancelled',
            plan_expiry: dayjs().format('YYYY-MM-DD'), removal_reason: result.value || 'Removed by admin',
            removed_by_admin: adminName,
          });
          try {
            await removePremiumFromUser(member.id, result.value);
            await createUserNotification({ user_id: member.id, title: '⚠️ Premium Deactivated', message: 'Your premium membership has been deactivated.', type: 'premium_removed' });
          } catch (e) { }
          message.success("Premium removed successfully");
          fetchPremium();
        } catch (error) { message.error("Failed to remove premium"); }
      }
    });
  };

  const handleViewHistory = async () => {
    setHistoryDrawerOpen(true);
    setHistoryLoading(true);
    try {
      const data = await getPremiumGrantHistory();
      setGrantHistory(Array.isArray(data) ? data : []);
    } catch (error) { setGrantHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const handleView = (record) => { setSelectedMember(record); setDetailsDrawerOpen(true); };
  const handleWhatsApp = (contact) => { if (contact) window.open(`https://wa.me/${contact.replace(/\D/g, "")}`, "_blank"); };

  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "profile_id", label: "Profile ID", format: (r) => r.profile_id || "N/A" },
    { key: "name", label: "Name", format: (r) => r.first_name || r.username || "N/A" },
    { key: "email", label: "Email", format: (r) => r.email || "N/A" },
    { key: "contact", label: "Contact", format: (r) => r.contact || "N/A" },
    { key: "total_plans", label: "Total Plans", format: (r) => r.total_plans || 1 },
    { key: "plans", label: "Plans", format: (r) => (r.all_plans || []).map(p => p.plan_name).join(", ") || r.plan_name },
    { key: "total_spent", label: "Total Spent", format: (r) => `₹${parseFloat(r.total_spent || r.plan_amount || 0).toFixed(2)}` },
    { key: "granted_by", label: "Granted By", format: (r) => r.granted_by_name || "Self Purchase" },
    { key: "payment_mode", label: "Payment", format: (r) => r.payment_mode || "Online" },
    { key: "expiry", label: "Latest Expiry", format: (r) => formatDate(r.plan_expiry) },
    { key: "status", label: "Status", format: (r) => isExpired(r.plan_expiry) ? "Expired" : "Active" },
  ];

  const handleExportCSV = (all = false) => exportToCSV(all ? members : filtered, exportColumns, "premium_members");
  const handleExportPDF = (all = false) => exportToPDF(all ? members : filtered, exportColumns, { title: "Premium Members Report", filename: "premium_members" });

  const stats = {
    total: members.length,
    active: members.filter(m => !isExpired(m.plan_expiry)).length,
    expired: members.filter(m => isExpired(m.plan_expiry)).length,
    expiringSoon: members.filter(m => isExpiringSoon(m.plan_expiry)).length,
    totalRevenue: members.reduce((sum, m) => sum + parseFloat(m.total_spent || m.plan_amount || 0), 0),
    adminGranted: members.filter(m => m.granted_by_admin).length,
    selfPurchased: members.filter(m => !m.granted_by_admin).length,
    multiPlanUsers: members.filter(m => (m.total_plans || 1) > 1).length,
  };

  const columns = [
    {
      title: "#", key: "sno", width: 40, fixed: "left",
      render: (_, __, i) => <span className="sno-cell">{(currentPage - 1) * pageSize + i + 1}</span>,
    },
    {
      title: "Profile ID", key: "profile_id", width: 100,
      render: (_, r) => <span className="ref-cell">{r.profile_id || 'N/A'}</span>,
    },
    {
      title: "Member", key: "name", width: 170,
      render: (_, r) => {
        const planCfg = getPlanConfig(r.plan_name);
        return (
          <div className="member-cell">
            <div className={`member-avatar ${planCfg.color}`}>
              {(r.first_name?.[0] || r.username?.[0] || "P").toUpperCase()}
              <div className="premium-badge-corner"><CrownOutlined /></div>
            </div>
            <div className="member-info">
              <div className="member-name">{r.first_name || r.username || "N/A"}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Mobile", key: "mobile", width: 120,
      render: (_, r) => <div className="mobile-call-cell"><span className="mobile-number"><PhoneOutlined /> {r.contact || "N/A"}</span></div>,
    },
    {
      title: "Plans", key: "plan", width: 120,
      render: (_, r) => {
        const allPlans = r.all_plans || [{ plan_name: r.plan_name, plan_type: r.plan_type }];
        const totalPlans = r.total_plans || 1;
        if (totalPlans === 1) {
          const cfg = getPlanConfig(allPlans[0].plan_name);
          return <Tag className={`plan-tag ${cfg.color}`} style={{ margin: 0 }}>{cfg.icon} {cfg.label}</Tag>;
        }
        return <div style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 800, background: '#f5f3ff', borderRadius: 8, textAlign: 'center', border: '1.5px solid #ddd6fe' }}>{totalPlans} Plans Active</div>;
      },
    },
    {
      title: "Amount", key: "amount", width: 130,
      render: (_, r) => {
        const allPlans = r.all_plans || [{ plan_amount: r.plan_amount }];
        const totalPlans = r.total_plans || 1;
        if (totalPlans === 1) {
          const amt = parseFloat(allPlans[0].plan_amount || 0);
          return <div style={{ fontWeight: 800, fontSize: 14, background: amt === 0 ? 'linear-gradient(135deg, #ec4899, #f472b6)' : 'linear-gradient(135deg, #10b981, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{amt === 0 ? '🎁 FREE' : `₹${amt.toLocaleString("en-IN")}`}</div>;
        }
        return (
          <div style={{ maxWidth: 120, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', gap: 3, overflowX: 'auto', padding: '1px 0 3px', maxWidth: 120 }} className="amount-scroll">
              {allPlans.map((plan, idx) => {
                const amt = parseFloat(plan.plan_amount || 0);
                const isFree = amt === 0;
                return (
                  <span key={idx} style={{ fontWeight: 700, fontSize: 9.5, color: isFree ? '#be185d' : '#047857', background: isFree ? 'linear-gradient(135deg, #fce7f3, #fbcfe8)' : 'linear-gradient(135deg, #ecfdf5, #d1fae5)', padding: '2px 7px', borderRadius: 999, border: `1px solid ${isFree ? '#f9a8d4' : '#6ee7b7'}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {isFree ? 'FREE' : `₹${amt}`}
                  </span>
                );
              })}
            </div>
          </div>
        );
      },
    },
    {
      title: "Expiry", key: "expiry", width: 110,
      render: (_, r) => {
        if (!r.plan_expiry) return <span>N/A</span>;
        const days = getDaysRemaining(r.plan_expiry);
        const expired = isExpired(r.plan_expiry);
        return (
          <div className="expiry-cell">
            <div className="expiry-date">{formatDate(r.plan_expiry)}</div>
            {expired ? <span className="expiry-badge expired">Expired</span> : days <= 7 ? <span className="expiry-badge warning">{days}d left</span> : <span className="expiry-badge active">{days}d left</span>}
          </div>
        );
      },
    },
    {
      title: "Status", key: "status", width: 85,
      render: (_, r) => {
        const expired = isExpired(r.plan_expiry);
        return <Tag className={`status-tag ${expired ? "expired" : "active"}`}>{expired ? <ClockCircleOutlined /> : <CheckCircleOutlined />} {expired ? "Expired" : "Active"}</Tag>;
      },
    },
    {
      title: "Actions", key: "action", width: 130, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} className="action-btn view" onClick={() => handleView(record)} /></Tooltip>
          <Tooltip title="Extend Plan"><Button size="small" icon={<PlusCircleOutlined />} style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }} onClick={() => { setExtendUser(record); setExtendModalOpen(true); }} /></Tooltip>
          <Tooltip title="WhatsApp"><Button size="small" icon={<WhatsAppOutlined />} className="action-btn whatsapp" onClick={() => handleWhatsApp(record.contact)} /></Tooltip>
          <Tooltip title="Remove Premium"><Button size="small" icon={<CloseCircleOutlined />} danger onClick={() => handleRemovePremium(record)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filtered.length})`, onClick: () => handleExportCSV(false) },
      { key: "2", label: `Export All (${members.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filtered.length})`, onClick: () => handleExportPDF(false) },
      { key: "2", label: `Export All (${members.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  return (
    <div className="premium-page">

      {/* ============ HEADER + STATS ============ */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          {/* LEFT SIDE: Title */}
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><CrownOutlined /></div>
              <div className="title-content">
                <h2 className="page-title">Premium Members</h2>
                <p className="page-subtitle">
                  {filtered.length} of {members.length} premium members
                  {stats.multiPlanUsers > 0 && (
                    <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>
                      🎉 {stats.multiPlanUsers} Multi-Plan
                    </Tag>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: All Buttons in a Single Row */}
          <div className="header-actions">
            <Tooltip title="Grant History">
              <Button icon={<HistoryOutlined />} onClick={handleViewHistory} className="icon-btn history-btn">
                <span>History</span>
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchPremium} loading={loading} className="icon-btn" />
            </Tooltip>
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn">
                <span>CSV</span>
              </Button>
            </Dropdown>
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn">
                <span>PDF</span>
              </Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<GiftOutlined />}
              onClick={handleOpenGrantModal}
              className="add-btn premium-add-btn"
              style={{
                background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                border: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(255,0,85,0.3)',
              }}
            >
              <span>Grant</span>
            </Button>

            {/* 📱 Mobile Only: Stats Toggle (Sirf Arrow 🔽) Same Line Mein */}
            {isMobile && (
              <button
                type="button"
                className="mobile-stats-icon-btn"
                onClick={() => setShowMobileStats(prev => !prev)}
              >
                {showMobileStats ? <UpOutlined /> : <DownOutlined />}
              </button>
            )}
          </div>
        </div>

                {/* Animated Smooth Stats Accordion (3 Columns) */}
        <div className={`mini-stats-bar ${isMobile && showMobileStats ? 'show' : ''}`}>
          
          <div className="mini-stat total">
            <CrownOutlined />
            <div className="stat-content">
              <b>{stats.total}</b> <span>Total</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat active">
            <CheckCircleOutlined />
            <div className="stat-content">
              <b>{stats.active}</b> <span>Active</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat expiring">
            <ClockCircleOutlined />
            <div className="stat-content">
              <b>{stats.expiringSoon}</b> <span>Expiring</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat expired">
            <FireOutlined />
            <div className="stat-content">
              <b>{stats.expired}</b> <span>Expired</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#8b5cf6' }}>
            <GiftOutlined />
            <div className="stat-content">
              <b>{stats.adminGranted}</b> <span>Admin Granted</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#3b82f6' }}>
            <WalletOutlined />
            <div className="stat-content">
              <b>{stats.selfPurchased}</b> <span>Self Purchase</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat" style={{ color: '#a855f7' }}>
            <TrophyOutlined />
            <div className="stat-content">
              <b>{stats.multiPlanUsers}</b> <span>Multi-Plan</span>
            </div>
          </div>
          <div className="mini-divider" />
          
          <div className="mini-stat revenue">
            <DollarOutlined />
            <div className="stat-content">
              <b>₹{stats.totalRevenue.toLocaleString("en-IN")}</b> <span>Revenue</span>
            </div>
          </div>

        </div>
      </div>

      {/* ============ FILTERS ============ */}
      <div className="quick-filters-bar">
        <div className="toolbar-right">
          <Input
            placeholder="Search by name, email, mobile, profile ID..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="toolbar-search"
            allowClear
          />
        </div>
      </div>



      {/* ============ TABLE / MOBILE CARDS ============ */}
      <div className="table-card">
        {isMobile ? (
          <div className="mobile-user-list">
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No premium members found</div>
            ) : (
              filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((r) => {
                const planCfg = getPlanConfig(r.plan_name);
                const expired = isExpired(r.plan_expiry);
                const days = getDaysRemaining(r.plan_expiry);
                const allPlans = r.all_plans || [{ plan_amount: r.plan_amount }];
                const totalPlans = r.total_plans || 1;

                return (
                  <div className="mobile-user-card" key={r.id}>
                    <div className="muc-header">
                      <span className="muc-id">{r.profile_id || 'N/A'}</span>
                      <span className={`muc-status ${expired ? 'expired' : 'active'}`}>
                        {expired ? '○ Expired' : '● Active'}
                      </span>
                    </div>

                    <div className="muc-body">
                      <div className={`muc-avatar ${planCfg.color}`}>
                        {(r.first_name?.[0] || r.username?.[0] || "P").toUpperCase()}
                        <div className="premium-badge-corner"><CrownOutlined /></div>
                      </div>
                      <div className="muc-info">
                        <h4 className="muc-name">{r.first_name || r.username || "N/A"}</h4>
                        <span className="muc-phone"><PhoneOutlined /> {r.contact || "N/A"}</span>
                      </div>
                    </div>

                    <div style={{ padding: '0 12px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        {totalPlans === 1 ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#faad14' }}>{planCfg.label}</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6' }}>{totalPlans} Plans Active</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: expired ? '#dc2626' : (days <= 7 ? '#fa8c16' : '#10b981') }}>
                        {expired ? 'Expired' : `${days} days left`}
                      </div>
                    </div>

                    <div className="muc-footer">
                      <div className="muc-plan">
                        {totalPlans === 1 ? (
                          parseFloat(allPlans[0].plan_amount || 0) === 0 ? '🎁 FREE' : `₹${parseFloat(allPlans[0].plan_amount || 0).toLocaleString("en-IN")}`
                        ) : (
                          `Total: ₹${parseFloat(r.total_spent || 0).toLocaleString("en-IN")}`
                        )}
                      </div>
                      <div className="muc-actions">
                        <button className="action-btn view" onClick={() => handleView(r)}><EyeOutlined /></button>
                        <button className="action-btn" style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }} onClick={() => { setExtendUser(r); setExtendModalOpen(true); }}><PlusCircleOutlined /></button>
                        <button className="action-btn whatsapp" onClick={() => handleWhatsApp(r.contact)}><WhatsAppOutlined /></button>
                        <button className="action-btn delete" onClick={() => handleRemovePremium(r)}><CloseCircleOutlined /></button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filtered.length}
              onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
              size="small"
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </div>
        ) : (
          <div className="desktop-table-wrapper">
            <Table
              rowKey={(record, index) => `member-${record.id}-${index}`}
              columns={columns}
              dataSource={filtered}
              loading={loading}
              size="small"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                size: "small",
                showTotal: (total) => `Total ${total} unique users`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: 1250 }}
            />
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* ✅ GRANT PREMIUM MODAL - With Extension Support */}
      {/* ============================================ */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 20,
            }}>
              <GiftOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Grant Premium to User</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Manually activate or extend premium membership</div>
            </div>
          </div>
        }
        open={grantModalOpen}
        onCancel={() => setGrantModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        centered
      >
        <Form form={grantForm} layout="vertical">
          {/* Admin Info Strip */}
          <div style={{
            display: 'flex', gap: 12, marginBottom: 20,
            padding: 12, background: '#fef3f7', borderRadius: 10,
            border: '1px solid #fbcfe8',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SafetyOutlined style={{ color: '#ec4899', fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>GRANTED BY</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{adminName} (Admin)</div>
              </div>
            </div>
          </div>

          {/* Select User - Admin can select ANY user */}
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Select User <span style={{ color: '#ef4444' }}>*</span></span>}
            required
          >
            <Select
              placeholder="🔍 Search user by name, mobile, or profile ID..."
              showSearch
              onChange={handleUserSelect}
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                return String(option.label || '').toLowerCase().includes(searchText);
              }}
              options={[
                // Free users first
                ...freeUsers.map(u => ({
                  value: u.id,
                  label: `${u.first_name || u.username || 'N/A'} - ${u.contact || 'No mobile'} - ${u.profile_id || 'No ID'} (Free)`,
                })),
                // Premium users (for extension)
                ...members.map(u => ({
                  value: u.id,
                  label: `${u.first_name || u.username || 'N/A'} - ${u.contact || 'No mobile'} - ${u.profile_id || 'No ID'} (Premium - ${u.total_plans || 1} plans)`,
                }))
              ]}
              size="large"
              notFoundContent="No users found"
            />
            {selectedUser && (
              <div style={{
                marginTop: 8, padding: 10, background: '#f0f9ff',
                borderRadius: 8, border: '1px solid #bae6fd',
                display: 'flex', gap: 12, alignItems: 'center',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700,
                }}>
                  {selectedUser.first_name?.[0] || selectedUser.username?.[0] || 'U'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#1e293b' }}>
                    {selectedUser.first_name || selectedUser.username}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    📱 {selectedUser.contact} | ID: {selectedUser.profile_id || 'N/A'}
                  </div>
                </div>
                {(selectedUser.is_premium || selectedUser.premium) ? (
                  <Tag color="green">⭐ Premium ({selectedUser.total_plans || 1} plans)</Tag>
                ) : (
                  <Tag color="orange">Free User</Tag>
                )}
              </div>
            )}
          </Form.Item>

          {/* Select Plan */}
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Select Plan <span style={{ color: '#ef4444' }}>*</span></span>}
            required
          >
            <Select
              placeholder="Choose a premium plan..."
              onChange={handlePlanSelect}
              size="large"
              options={premiumPlans.map(p => ({
                value: p.id,
                label: `${PLAN_TYPE_CONFIG[p.plan_type]?.icon || '👑'} ${p.plan_name} - ₹${p.price} (${p.duration} days)`,
              }))}
            />
            {selectedPlan && (
              <div style={{
                marginTop: 8, padding: 12, borderRadius: 10,
                background: PLAN_TYPE_CONFIG[selectedPlan.plan_type]?.color + '15',
                border: `1.5px solid ${PLAN_TYPE_CONFIG[selectedPlan.plan_type]?.color}40`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{selectedPlan.plan_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Duration: {selectedPlan.duration} days
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: '#ff0055' }}>
                      ₹{selectedPlan.price?.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Form.Item>

          {/* ✅ EXTENSION WARNING - If user already has this plan */}
          {existingPlan && (
            <Alert
              message="⚠️ Extension Mode Detected"
              description={
                <div>
                  <p style={{ margin: '4px 0' }}>
                    This user <strong>already has</strong> "{selectedPlan?.plan_name}" plan.
                  </p>
                  <p style={{ margin: '4px 0', fontSize: 12 }}>
                    <strong>Current Expiry:</strong> {formatDate(existingPlan.plan_expiry)}<br />
                    <strong>Will Extend To:</strong> {dayjs(existingPlan.plan_expiry).add(selectedPlan?.duration || 30, 'day').format('DD MMM YYYY')}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: 12, color: '#059669' }}>
                    ✅ Duration + Amount + Limits will be added to existing plan
                  </p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Payment Mode */}
          <Form.Item label={<span style={{ fontWeight: 700 }}>Payment Mode</span>}>
            <Radio.Group value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {PAYMENT_MODES.map(mode => (
                <Radio.Button key={mode.value} value={mode.value}
                  style={{
                    textAlign: 'center', padding: '4px 8px',
                    borderRadius: 8, fontWeight: 600, fontSize: 12,
                  }}>
                  {mode.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          {/* Amount + Dates Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item label={<span style={{ fontWeight: 700 }}>Amount (₹)</span>}>
              <InputNumber
                value={paymentMode === 'gift' ? 0 : customAmount}
                onChange={setCustomAmount}
                min={0}
                disabled={paymentMode === 'gift'}
                style={{ width: '100%' }}
                prefix="₹"
                size="large"
              />
            </Form.Item>

            <Form.Item label={<span style={{ fontWeight: 700 }}>Start Date</span>}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                style={{ width: '100%' }}
                size="large"
                allowClear={false}
              />
            </Form.Item>

            <Form.Item label={<span style={{ fontWeight: 700 }}>End Date (Auto)</span>}>
              <Input
                value={calculateEndDate() ? calculateEndDate().format('DD MMM YYYY') : 'Select plan first'}
                readOnly
                size="large"
                style={{ background: '#f8fafc', fontWeight: 600, color: '#1e293b' }}
              />
            </Form.Item>
          </div>

          {/* Admin Note */}
          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Admin Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</span></span>}
            name="admin_note"
          >
            <TextArea
              rows={2}
              placeholder="Add a note about this premium grant (visible in history)..."
              maxLength={200}
              showCount
            />
          </Form.Item>

          {/* Info Alert */}
          <Alert
            message="What will happen?"
            description={
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                {existingPlan ? (
                  <>
                    <li><strong>Extend Mode:</strong> Duration will be added to existing plan</li>
                    <li>Amount will be added to existing total</li>
                    <li>Contact/Profile view limits will be added</li>
                    <li>Transaction record created ({paymentMode === 'gift' ? 'as Free/Gift' : `Amount ₹${customAmount || 0}`})</li>
                  </>
                ) : (
                  <>
                    <li><strong>Grant Mode:</strong> New premium plan will be added</li>
                    <li>User's account will be upgraded to premium</li>
                    <li>Transaction record will be created ({paymentMode === 'gift' ? 'as Free/Gift' : `Amount ₹${customAmount || 0}`})</li>
                  </>
                )}
                <li>SMS notification will be sent to user</li>
                <li>In-app notification will appear on user's dashboard</li>
                <li>Grant will be logged in history for tracking</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button size="large" onClick={() => setGrantModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              loading={grantLoading}
              onClick={handleGrantPremium}
              icon={<CrownOutlined />}
              style={{
                background: existingPlan
                  ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                  : 'linear-gradient(135deg, #ff0055, #ff4d88)',
                border: 'none',
                fontWeight: 700,
                padding: '0 32px',
                boxShadow: existingPlan
                  ? '0 4px 12px rgba(245,158,11,0.3)'
                  : '0 4px 12px rgba(255,0,85,0.3)',
              }}
            >
              {existingPlan ? '⭐ Extend Plan Now' : '⭐ Grant Premium Now'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ============================================ */}
      {/* ✅ EXTEND PREMIUM MODAL (Quick Extend) */}
      {/* ============================================ */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PlusCircleOutlined style={{ color: '#16a34a', fontSize: 20 }} />
          <span>Extend Premium</span>
        </div>}
        open={extendModalOpen}
        onCancel={() => setExtendModalOpen(false)}
        onOk={handleExtendPremium}
        okText="Extend Now"
        okButtonProps={{ style: { background: '#16a34a', border: 'none' } }}
        centered
      >
        {extendUser && (
          <div>
            <Alert
              message={`Extending: ${extendUser.first_name || extendUser.username}`}
              description={`Current expiry: ${formatDate(extendUser.plan_expiry) || 'N/A'}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form.Item label={<span style={{ fontWeight: 700 }}>Extend By (Days)</span>}>
              <Radio.Group value={extendDays} onChange={e => setExtendDays(e.target.value)}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <Radio.Button value={7}>7 Days</Radio.Button>
                <Radio.Button value={15}>15 Days</Radio.Button>
                <Radio.Button value={30}>1 Month</Radio.Button>
                <Radio.Button value={90}>3 Months</Radio.Button>
                <Radio.Button value={180}>6 Months</Radio.Button>
                <Radio.Button value={365}>1 Year</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <div style={{
              padding: 12, background: '#f0fdf4', borderRadius: 8,
              border: '1px solid #bbf7d0', marginTop: 12,
            }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>NEW EXPIRY DATE</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#16a34a', marginTop: 4 }}>
                {extendUser.plan_expiry
                  ? dayjs(extendUser.plan_expiry).add(extendDays, 'day').format('DD MMM YYYY')
                  : dayjs().add(extendDays, 'day').format('DD MMM YYYY')
                }
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* ✅ GRANT HISTORY DRAWER */}
      {/* ============================================ */}
      <Drawer
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HistoryOutlined /> Premium Grant History
        </div>}
        placement="right"
        width={500}
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : grantHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            <HistoryOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <p>No grant history available yet</p>
          </div>
        ) : (
          <div>
            {grantHistory.map((h, i) => (
              <div key={i} style={{
                padding: 12, marginBottom: 10,
                background: '#f8fafc', borderRadius: 10,
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong>{h.user_name || 'User'}</strong>
                  <Tag color={h.action_type === 'extend' ? 'orange' : 'pink'}>
                    {h.action_type === 'extend' ? '⭐ Extended' : '🎁 Granted'} - {h.plan_name}
                  </Tag>
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {h.action_type === 'extend' ? 'Extended' : 'Granted'} by: <strong>{h.admin_name}</strong>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  {formatDate(h.created_date)} • ₹{h.amount} • {h.payment_mode}
                </div>
                {h.admin_note && (
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 6, fontStyle: 'italic' }}>
                    "{h.admin_note}"
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Drawer>

      {/* ============================================ */}
      {/* ✅ DETAILS DRAWER - Multi-Plan Support */}
      {/* ============================================ */}
      <Drawer
        title={<div className="drawer-title"><CrownOutlined /> Premium Member Details</div>}
        placement="right"
        width={480}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
      >
        {selectedMember && (
          <div className="member-details">
            <div className="member-detail-header">
              <div className={`detail-avatar ${getPlanConfig(selectedMember.plan_name).color}`}>
                {(selectedMember.first_name?.[0] || selectedMember.username?.[0] || "P").toUpperCase()}
                <div className="premium-badge-corner-lg"><CrownOutlined /></div>
              </div>
              <h2>{selectedMember.first_name || selectedMember.username || "N/A"}</h2>
              <p className="detail-ref">#{selectedMember.profile_id || 'N/A'}</p>
              <div className="detail-tags">
                <Tag className={`plan-tag ${getPlanConfig(selectedMember.plan_name).color}`}>
                  {getPlanConfig(selectedMember.plan_name).icon} {getPlanConfig(selectedMember.plan_name).label}
                </Tag>
                {selectedMember.granted_by_admin && (
                  <Tag color="purple">🎁 Admin Granted</Tag>
                )}
                {(selectedMember.total_plans || 1) > 1 && (
                  <Tag color="magenta">🎉 Multi-Plan</Tag>
                )}
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h4>
                <CrownOutlined /> Plans ({selectedMember.total_plans || 1})
              </h4>

              {/* ✅ Individual Plans - Each plan separate card */}
              {(() => {
                const allPlans = selectedMember.all_plans || [selectedMember];

                // Group same plans together
                const planGroups = {};
                allPlans.forEach(plan => {
                  const key = plan.plan_name || 'Premium';
                  if (!planGroups[key]) {
                    planGroups[key] = {
                      ...plan,
                      count: 1,
                      total_amount: parseFloat(plan.plan_amount || 0),
                      purchases: [plan],
                    };
                  } else {
                    planGroups[key].count += 1;
                    planGroups[key].total_amount += parseFloat(plan.plan_amount || 0);
                    planGroups[key].purchases.push(plan);
                    // Update expiry to latest
                    if (plan.plan_expiry > planGroups[key].plan_expiry) {
                      planGroups[key].plan_expiry = plan.plan_expiry;
                    }
                  }
                });

                return Object.values(planGroups).map((group, idx) => {
                  const cfg = getPlanConfig(group.plan_name);
                  const typeCfg = PLAN_TYPE_CONFIG[group.plan_type] || {};
                  const expired = isExpired(group.plan_expiry);

                  return (
                    <div key={idx} style={{
                      background: '#fff',
                      border: `2px solid ${expired ? '#fecaca' : '#e2e8f0'}`,
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}>
                      {/* Plan Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Tag className={`plan-tag ${cfg.color}`} style={{ fontSize: 12, margin: 0 }}>
                            {cfg.icon} {group.plan_name}
                          </Tag>
                          {group.count > 1 && (
                            <Tag color="purple" style={{ fontWeight: 800, margin: 0 }}>
                              x{group.count}
                            </Tag>
                          )}
                        </div>
                        {group.plan_type && typeCfg.icon && (
                          <span style={{ fontSize: 10, color: typeCfg.color, fontWeight: 700 }}>
                            {typeCfg.icon} {typeCfg.label}
                          </span>
                        )}
                      </div>

                      {/* Purchase History - Each purchase separately */}
                      {group.purchases.map((purchase, pIdx) => (
                        <div key={pIdx} style={{
                          background: pIdx % 2 === 0 ? '#f8fafc' : '#fff',
                          border: '1px solid #f1f5f9',
                          borderRadius: 8,
                          padding: 10,
                          marginBottom: 6,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                              Purchase #{pIdx + 1}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: parseFloat(purchase.plan_amount || 0) === 0 ? '#ec4899' : '#ff0055' }}>
                              {parseFloat(purchase.plan_amount || 0) === 0 ? '🎁 FREE' : `₹${parseFloat(purchase.plan_amount || 0).toLocaleString("en-IN")}`}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
                            <div>
                              <div style={{ color: '#94a3b8', fontSize: 10 }}>Started</div>
                              <div style={{ fontWeight: 600 }}>{formatDate(purchase.plan_start_date)}</div>
                            </div>
                            <div>
                              <div style={{ color: '#94a3b8', fontSize: 10 }}>Expires</div>
                              <div style={{ color: isExpired(purchase.plan_expiry) ? '#dc2626' : '#10b981', fontWeight: 600 }}>
                                {formatDate(purchase.plan_expiry)}
                              </div>
                            </div>
                          </div>

                          {/* Payment mode */}
                          {purchase.payment_mode && (
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                              {purchase.payment_mode === 'gift' ? '🎁 Free' : `💳 via ${purchase.payment_mode}`}
                            </div>
                          )}

                          {/* Granted by */}
                          {purchase.granted_by_name && (
                            <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 4 }}>
                              👑 by {purchase.granted_by_name}
                              {purchase.granted_by_role && ` (${purchase.granted_by_role})`}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Plan Limits */}
                      {(group.contact_view_limit > 0 || group.profile_view_limit > 0) && (
                        <div style={{
                          padding: 8, background: '#f0f9ff',
                          borderRadius: 6, fontSize: 11, color: '#0369a1', marginTop: 4,
                        }}>
                          {group.contact_view_limit > 0 && (
                            <div>📱 Total Contact Views: <strong>{group.contact_view_limit}</strong></div>
                          )}
                          {group.profile_view_limit > 0 && (
                            <div>👤 Total Profile Views: <strong>{group.profile_view_limit}</strong></div>
                          )}
                        </div>
                      )}

                      {/* Summary for this plan */}
                      {group.count > 1 && (
                        <div style={{
                          marginTop: 8, padding: 8,
                          background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                          borderRadius: 6, fontSize: 11,
                          display: 'flex', justifyContent: 'space-between',
                        }}>
                          <span style={{ color: '#7c3aed', fontWeight: 700 }}>
                            📊 {group.count} times purchased
                          </span>
                          <span style={{ color: '#7c3aed', fontWeight: 800 }}>
                            Total: ₹{group.total_amount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {/* Admin Note (latest) */}
                      {group.admin_note && (
                        <div style={{
                          marginTop: 6, padding: 6,
                          background: '#fef3f7', borderRadius: 6,
                          fontSize: 10, fontStyle: 'italic', color: '#1e293b',
                        }}>
                          📝 {group.admin_note}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}

              {/* Total Summary - Grand Total */}
              {(selectedMember.total_plans || 1) > 1 && (
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                  border: '1.5px solid #86efac',
                  borderRadius: 10, padding: 12, marginTop: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: '#065f46', fontWeight: 700 }}>💰 GRAND TOTAL</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: '#047857' }}>
                      ₹{parseFloat(selectedMember.total_spent || 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#059669', marginTop: 4 }}>
                    Across {selectedMember.total_plans} purchase(s) of {Object.keys(
                      (selectedMember.all_plans || []).reduce((acc, p) => { acc[p.plan_name] = true; return acc; }, {})
                    ).length} unique plan(s)
                  </div>
                </div>
              )}
            </div>

            <Divider />

            <div className="detail-section">
              <h4><UserOutlined /> Contact Info</h4>
              <div className="detail-item"><MailOutlined /><strong>Email</strong><span>{selectedMember.email || "N/A"}</span></div>
              <div className="detail-item"><PhoneOutlined /><strong>Mobile</strong><span>{selectedMember.contact || "N/A"}</span></div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default PremiumMembers;